import React, { lazy, useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { renderRoutes } from 'react-router-config';
import { Redirect } from 'react-router-dom';

import { ConfigurationInterface, UserInterface } from '@e3labs/isa-data-dictionary';

import MainLayout from '~/layouts/Main';
import AuthLayout from '~/layouts/Auth';
import ErrorLayout from '~/layouts/Error';

import { historyRef } from '~/services/history';
import { ROUTES_VALIDATIONS, USER_ROLE } from '~/constants';

import RoutesMiddleware from './middleware';

const Routes: React.FunctionComponent<any> = ({
  user,
  configuration
}: {
  user: UserInterface;
  configuration: ConfigurationInterface;
}) => {
  const middleware = new RoutesMiddleware();
  const routes = useMemo(() => {
    return [
      {
        path: '/auth',
        component: AuthLayout,
        routes: [
          {
            path: '/auth/login',
            exact: true,
            component: lazy(() => import('~/pages/auth/Login'))
          },
          {
            path: '/auth/reset-password',
            exact: true,
            component: lazy(() => import('~/pages/auth/ResetPassword'))
          },
          {
            component: () => <Redirect to="/errors/error-404" />
          }
        ]
      },
      {
        path: '/errors',
        component: ErrorLayout,
        routes: [
          {
            path: '/errors/error-401',
            exact: true,
            component: lazy(() => import('~/pages/error/Error401'))
          },
          {
            path: '/errors/error-404',
            exact: true,
            component: lazy(() => import('~/pages/error/Error404'))
          },
          {
            path: '/errors/error-500',
            exact: true,
            component: lazy(() => import('~/pages/error/Error500'))
          },
          {
            component: () => <Redirect to="/errors/error-404" />
          }
        ]
      },
      {
        route: '*',
        component: MainLayout,
        routes: [
          {
            path: '/dashboard/recommendations',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Dashboard/pages/Recommendations')
              )
            )
          },
          {
            path: '/dashboard/indicators',
            exact: true,
            component: lazy(() => import('~/pages/main/Dashboard/pages/Indicators'))
          },
          {
            path: '/dashboard/analytics',
            exact: true,
            component: lazy(() => import('~/pages/main/Dashboard/pages/Analytics'))
          },
          {
            path: '/dashboard/custom/:dashboardId',
            exact: true,
            component: lazy(() => import('~/pages/main/Dashboard/pages/DynamicDashboard'))
          },
          {
            path: '/dashboard/client-visit',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MANAGER, USER_ROLE.SUPERVISOR],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Dashboard/pages/ClientVisit')
              )
            )
          },
          {
            path: '/dashboard/telemetria',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Dashboard/pages/Telemetria')
              )
            )
          },
          {
            path: '/dashboard/positivation-sellers',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Dashboard/pages/PositivationSellers')
              )
            )
          },
          {
            path: '/dashboard/efficiency-sales-potential',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Dashboard/pages/EfficiencySalesPotential')
              )
            )
          },
          {
            path: '/configuration/app',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Configurations/AppConfiguration')
              )
            )
          },
          {
            path: '/configuration/infrastructure',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Configurations/InfrastructureConfiguration')
              )
            )
          },
          {
            path: '/tenant',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Tenant')
              )
            )
          },
          {
            path: '/notifications',
            exact: true,
            component: lazy(() => import('~/pages/main/Notification'))
          },
          {
            path: '/notifications/send',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.MANAGER, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Notification/Send')
              )
            )
          },
          {
            path: '/order',
            exact: true,
            component: lazy(() => import('~/pages/main/Order/OrderList'))
          },
          {
            path: '/order/new/step1',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.VENDOR, USER_ROLE.TELEMARKETING],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.allowOrderWebRegistration
                },
                import('~/pages/main/Order/OrderCreate')
              )
            )
          },
          {
            path: '/order/:id/step1',
            exact: true,
            component: lazy(() => import('~/pages/main/Order/OrderCreate'))
          },
          {
            path: '/order/:id/:slug/step1',
            exact: true,
            component: lazy(() => import('~/pages/main/Order/OrderCreate'))
          },
          {
            path: '/order/view/:id',
            exact: true,
            component: lazy(() => import('~/pages/main/Order/OrderDetail'))
          },
          {
            path: '/products',
            exact: true,
            component: lazy(() => import('~/pages/main/Products'))
          },
          {
            path: '/sales-tracking',
            exact: true,
            component: lazy(() => import('~/pages/main/SalesTracking'))
          },
          {
            path: '/engine/rewards',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Rewards/RewadsList')
              )
            )
          },
          {
            path: '/engine/rewards/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Rewards/RewardsForm')
              )
            )
          },
          {
            path: '/engine/rewards/edit/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Rewards/RewardsForm')
              )
            )
          },

          {
            path: '/engine/trading',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Trading/TradingList')
              )
            )
          },
          {
            path: '/engine/trading/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Trading/TradingForm')
              )
            )
          },
          {
            path: '/engine/trading/edit/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Trading/TradingForm')
              )
            )
          },
          {
            path: '/engine/sponsored',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Sponsored/SponsoredList')
              )
            )
          },
          {
            path: '/engine/sponsored/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Sponsored/SponsoredForm')
              )
            )
          },
          {
            path: '/engine/sponsored/edit/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Sponsored/SponsoredForm')
              )
            )
          },
          {
            path: '/engine/promotion',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Promotion/PromotionList')
              )
            )
          },
          {
            path: '/engine/promotion/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Promotion/PromotionForm')
              )
            )
          },
          {
            path: '/engine/promotion/edit/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Promotion/PromotionForm')
              )
            )
          },
          {
            path: '/engine/promotion/edit/import/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Promotion/PromotionsImportReport')
              )
            )
          },
          {
            path: '/engine/mandatory-mix',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES, ROUTES_VALIDATIONS.USE_MANDATORY_MIX],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MANAGER],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.useMandatoryMix
                },
                import('~/pages/main/MandatoryMix/MandatoryMixList')
              )
            )
          },
          {
            path: '/engine/mandatory-mix/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES, ROUTES_VALIDATIONS.USE_MANDATORY_MIX],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MANAGER],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.useMandatoryMix
                },
                import('~/pages/main/MandatoryMix/MandatoryMixForm')
              )
            )
          },
          {
            path: '/engine/mandatory-mix/edit/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES, ROUTES_VALIDATIONS.USE_MANDATORY_MIX],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MANAGER],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.useMandatoryMix
                },
                import('~/pages/main/MandatoryMix/MandatoryMixForm')
              )
            )
          },
          {
            path: '/register/reason',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MANAGER, USER_ROLE.SUPERVISOR],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/Reason/ReasonList')
              )
            )
          },
          {
            path: '/register/reason/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MANAGER, USER_ROLE.SUPERVISOR],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/Reason/ReasonForm')
              )
            )
          },
          {
            path: '/register/reason/edit/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MANAGER, USER_ROLE.SUPERVISOR],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/Reason/ReasonForm')
              )
            )
          },
          {
            path: '/register/order-type',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/OrderType/OrderTypeList')
              )
            )
          },
          {
            path: '/register/order-type/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/OrderType/OrderTypeForm')
              )
            )
          },
          {
            path: '/register/order-type/edit/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/OrderType/OrderTypeForm')
              )
            )
          },
          {
            path: '/register/order-split',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/OrderSplit/OrderSplitList')
              )
            )
          },
          {
            path: '/register/order-split/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/OrderSplit/OrderSplitForm')
              )
            )
          },
          {
            path: '/register/order-split/edit/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/OrderSplit/OrderSplitForm')
              )
            )
          },
          {
            path: '/register/product-photos',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/ProductPhotos/ProductPhotosList')
              )
            )
          },
          {
            path: '/register/product-photos/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/ProductPhotos/ProductPhotosForm')
              )
            )
          },
          {
            path: '/register/product-photos/edit/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/ProductPhotos/ProductPhotosForm')
              )
            )
          },
          {
            path: '/register/client',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES, ROUTES_VALIDATIONS.ALLOW_COSTUMER_REGISTRATION],
                {
                  requiredRoles: [
                    USER_ROLE.VENDOR,
                    USER_ROLE.TELEMARKETING,
                    USER_ROLE.SUPERVISOR,
                    USER_ROLE.MANAGER,
                    USER_ROLE.DIRECTOR
                  ],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.allowCustomerRegistration
                },
                import('~/pages/main/Registers/Client/ClientList')
              )
            )
          },
          {
            path: '/register/client/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES, ROUTES_VALIDATIONS.ALLOW_COSTUMER_REGISTRATION],
                {
                  requiredRoles: [USER_ROLE.VENDOR, USER_ROLE.TELEMARKETING],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.allowCustomerRegistration
                },
                import('~/pages/main/Registers/Client/ClientForm')
              )
            )
          },
          {
            path: '/register/client/edit/:id/:typeList?',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES, ROUTES_VALIDATIONS.ALLOW_COSTUMER_REGISTRATION],
                {
                  requiredRoles: [
                    USER_ROLE.VENDOR,
                    USER_ROLE.TELEMARKETING,
                    USER_ROLE.SUPERVISOR,
                    USER_ROLE.MANAGER,
                    USER_ROLE.DIRECTOR
                  ],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.allowCustomerRegistration
                },
                import('~/pages/main/Registers/Client/ClientForm')
              )
            )
          },
          {
            path: '/register/client-checklist',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [
                    USER_ROLE.DIRECTOR,
                    USER_ROLE.TECH,
                    USER_ROLE.MANAGER,
                    USER_ROLE.SUPERVISOR,
                    USER_ROLE.MAINTENANCE
                  ],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.useClientChecklist
                },

                import('~/pages/main/Registers/ClientChecklist/ClientChecklistList')
              )
            )
          },
          {
            path: '/register/client-checklist/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [
                    USER_ROLE.DIRECTOR,
                    USER_ROLE.TECH,
                    USER_ROLE.MANAGER,
                    USER_ROLE.SUPERVISOR,
                    USER_ROLE.MAINTENANCE
                  ],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.useClientChecklist
                },
                import('~/pages/main/Registers/ClientChecklist/ClientChecklistForm')
              )
            )
          },
          {
            path: '/register/client-checklist/edit/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [
                    USER_ROLE.DIRECTOR,
                    USER_ROLE.TECH,
                    USER_ROLE.MANAGER,
                    USER_ROLE.SUPERVISOR,
                    USER_ROLE.MAINTENANCE
                  ],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.useClientChecklist
                },
                import('~/pages/main/Registers/ClientChecklist/ClientChecklistForm')
              )
            )
          },
          {
            path: '/register/daily-route',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MANAGER, USER_ROLE.SUPERVISOR],
                  currentRole: user?.userRole
                },

                import('~/pages/main/Registers/DailyRoute/DailyRouteList')
              )
            )
          },
          {
            path: '/register/daily-route/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MANAGER, USER_ROLE.SUPERVISOR],
                  currentRole: user?.userRole
                },

                import('~/pages/main/Registers/DailyRoute/DailyRouteForm')
              )
            )
          },
          {
            path: '/register/daily-route/edit/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MANAGER, USER_ROLE.SUPERVISOR],
                  currentRole: user?.userRole
                },

                import('~/pages/main/Registers/DailyRoute/DailyRouteForm')
              )
            )
          },
          {
            path: '/register/routing/planning/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [
                    USER_ROLE.DIRECTOR,
                    USER_ROLE.TECH,
                    USER_ROLE.MANAGER,
                    USER_ROLE.SUPERVISOR,
                    USER_ROLE.VENDOR,
                    USER_ROLE.MAINTENANCE
                  ],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/Routing/RoutePlanning')
              )
            )
          },
          {
            path: '/register/routing/planning',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [
                    USER_ROLE.DIRECTOR,
                    USER_ROLE.TECH,
                    USER_ROLE.MANAGER,
                    USER_ROLE.SUPERVISOR,
                    USER_ROLE.VENDOR,
                    USER_ROLE.MAINTENANCE
                  ],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/Routing/RoutePlanning')
              )
            )
          },
          {
            path: '/register/routing/list',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [
                    USER_ROLE.DIRECTOR,
                    USER_ROLE.TECH,
                    USER_ROLE.MANAGER,
                    USER_ROLE.SUPERVISOR,
                    USER_ROLE.VENDOR,
                    USER_ROLE.MAINTENANCE
                  ],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/Routing/RouteList')
              )
            )
          },

          {
            path: '/register/product-technical-data',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [
                    USER_ROLE.DIRECTOR,
                    USER_ROLE.TECH,
                    USER_ROLE.MANAGER,
                    USER_ROLE.SUPERVISOR,
                    USER_ROLE.MAINTENANCE
                  ],
                  currentRole: user?.userRole
                },

                import('~/pages/main/Registers/ProductTechnical/ProductTechnicalDataList')
              )
            )
          },
          {
            path: '/register/product-technical-data/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [
                    USER_ROLE.DIRECTOR,
                    USER_ROLE.TECH,
                    USER_ROLE.MANAGER,
                    USER_ROLE.SUPERVISOR,
                    USER_ROLE.MAINTENANCE
                  ],
                  currentRole: user?.userRole
                },

                import('~/pages/main/Registers/ProductTechnical/ProductTechnicalDataForm')
              )
            )
          },
          {
            path: '/register/product-technical-data/edit/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [
                    USER_ROLE.DIRECTOR,
                    USER_ROLE.TECH,
                    USER_ROLE.MANAGER,
                    USER_ROLE.SUPERVISOR,
                    USER_ROLE.MAINTENANCE
                  ],
                  currentRole: user?.userRole
                },

                import('~/pages/main/Registers/ProductTechnical/ProductTechnicalDataForm')
              )
            )
          },
          {
            path: '/load-control',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES, ROUTES_VALIDATIONS.LOAD_CONTROL],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.useLoadControl
                },
                import('~/pages/main/LoadControl/LoadControlList')
              )
            )
          },
          {
            path: '/load-control/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES, ROUTES_VALIDATIONS.LOAD_CONTROL],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.useLoadControl
                },
                import('~/pages/main/LoadControl/LoadControlFormNew')
              )
            )
          },
          {
            path: '/load-control/edit/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES, ROUTES_VALIDATIONS.LOAD_CONTROL],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.useLoadControl
                },
                import('~/pages/main/LoadControl/LoadControlFormEdit')
              )
            )
          },
          {
            path: '/register/load-type',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES, ROUTES_VALIDATIONS.LOAD_CONTROL],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.useLoadControl
                },
                import('~/pages/main/Registers/LoadType/LoadTypeList')
              )
            )
          },
          {
            path: '/register/load-type/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES, ROUTES_VALIDATIONS.LOAD_CONTROL],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.useLoadControl
                },
                import('~/pages/main/Registers/LoadType/LoadTypeForm')
              )
            )
          },
          {
            path: '/register/load-type/edit/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES, ROUTES_VALIDATIONS.LOAD_CONTROL],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.useLoadControl
                },
                import('~/pages/main/Registers/LoadType/LoadTypeForm')
              )
            )
          },
          {
            path: '/register/justifications',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MANAGER, USER_ROLE.SUPERVISOR],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.allowListJustification
                },
                import('~/pages/main/Registers/Justifications/JustificationsList')
              )
            )
          },
          {
            path: '/register/justifications/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MANAGER, USER_ROLE.SUPERVISOR],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.allowListJustification
                },
                import('~/pages/main/Registers/Justifications/JustificationsForm')
              )
            )
          },
          {
            path: '/register/justifications/edit/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MANAGER, USER_ROLE.SUPERVISOR],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.allowListJustification
                },
                import('~/pages/main/Registers/Justifications/JustificationsForm')
              )
            )
          },
          {
            path: '/reports/rating',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [
                    USER_ROLE.DIRECTOR,
                    USER_ROLE.TECH,
                    USER_ROLE.MANAGER,
                    USER_ROLE.SUPERVISOR,
                    USER_ROLE.MAINTENANCE
                  ],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.useClientRating
                },
                import('~/pages/main/Ratings')
              )
            )
          },
          {
            path: '/reports/client-visit',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [
                    USER_ROLE.DIRECTOR,
                    USER_ROLE.TECH,
                    USER_ROLE.MANAGER,
                    USER_ROLE.SUPERVISOR,
                    USER_ROLE.MAINTENANCE
                  ],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.allowVendorTracking || configuration?.allowManualVendorTracking
                },
                import('~/pages/main/ClientVisit')
              )
            )
          },
          {
            path: '/reports/control-notifications',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [
                    USER_ROLE.DIRECTOR,
                    USER_ROLE.TECH,
                    USER_ROLE.MANAGER,
                    USER_ROLE.SUPERVISOR,
                    USER_ROLE.MAINTENANCE
                  ],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.usePersistentNotification
                },
                import('~/pages/main/ControlNotifications')
              )
            )
          },
          {
            path: '/reports/client-checklist-report',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [
                    USER_ROLE.DIRECTOR,
                    USER_ROLE.TECH,
                    USER_ROLE.MANAGER,
                    USER_ROLE.SUPERVISOR,
                    USER_ROLE.MAINTENANCE
                  ],
                  currentRole: user?.userRole,
                  extraValidation: configuration?.useClientChecklist
                },
                import('~/pages/main/ClientReportChecklist')
              )
            )
          },
          {
            path: '/reports/promotions-kit',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [
                    USER_ROLE.DIRECTOR,
                    USER_ROLE.TECH,
                    USER_ROLE.MANAGER,
                    USER_ROLE.SUPERVISOR,
                    USER_ROLE.MAINTENANCE
                  ],
                  currentRole: user?.userRole
                },
                import('~/pages/main/ReportPromotionKit')
              )
            )
          },
          {
            path: '/reports/daily-route',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [
                    USER_ROLE.DIRECTOR,
                    USER_ROLE.TECH,
                    USER_ROLE.MANAGER,
                    USER_ROLE.SUPERVISOR,
                    USER_ROLE.MAINTENANCE
                  ],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Reports/DailyRouteReport')
              )
            )
          },
          {
            path: '/reports/vendor-client-visit',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [
                    USER_ROLE.DIRECTOR,
                    USER_ROLE.TECH,
                    USER_ROLE.MANAGER,
                    USER_ROLE.SUPERVISOR,
                    USER_ROLE.MAINTENANCE
                  ],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Reports/ClientVisitsAndSalesReport')
              )
            )
          },
          {
            path: '/reports/commission',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.VENDOR],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Reports/CommissionReport')
              )
            )
          },
          {
            path: '/reports/client-position',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.VENDOR],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Reports/ClientPositionReport')
              )
            )
          },
          {
            path: '/reports/routed-clients',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [
                    USER_ROLE.DIRECTOR,
                    USER_ROLE.TECH,
                    USER_ROLE.MANAGER,
                    USER_ROLE.SUPERVISOR,
                    USER_ROLE.MAINTENANCE
                  ],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Reports/RoutedClientsReport')
              )
            )
          },
          {
            path: '/reports/product-without-picture',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [
                    USER_ROLE.DIRECTOR,
                    USER_ROLE.TECH,
                    USER_ROLE.MANAGER,
                    USER_ROLE.SUPERVISOR,
                    USER_ROLE.MAINTENANCE
                  ],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Reports/ProductWithoutPictureReport')
              )
            )
          },
          {
            path: '/access/team',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Team/TeamList')
              )
            )
          },
          {
            path: '/access/team/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Team/TeamForm')
              )
            )
          },
          {
            path: '/access/team/edit/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Team/TeamForm')
              )
            )
          },
          {
            path: '/access/user',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/User/UserList')
              )
            )
          },
          {
            path: '/access/user/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/User/UserForm')
              )
            )
          },
          {
            path: '/access/user/edit/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/User/UserForm')
              )
            )
          },
          {
            path: '/access/profile',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/AccessProfile/AccessProfileList')
              )
            )
          },
          {
            path: '/access/profile/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/AccessProfile/AccessProfileForm')
              )
            )
          },
          {
            path: '/access/profile/edit/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/AccessProfile/AccessProfileForm')
              )
            )
          },
          {
            path: '/profile',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/AccessProfile/AccessProfileForm')
              )
            )
          },
          {
            path: '/register/product-kit/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/ProductKit/ProductKitForm')
              )
            )
          },
          {
            path: '/register/product-bonification',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/BonificationProducts/BonificationProductsList')
              )
            )
          },
          {
            path: '/register/product-bonification/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/BonificationProducts/BonificationProductsForm')
              )
            )
          },
          {
            path: '/register/product-bonification/edit/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/BonificationProducts/BonificationProductsForm')
              )
            )
          },
          {
            path: '/register/focus-supplier',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/FocusSupplier/FocusSupplierList')
              )
            )
          },
          {
            path: '/register/focus-supplier/new',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/FocusSupplier/FocusSupplierForm')
              )
            )
          },
          {
            path: '/register/focus-supplier/edit/:id',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Registers/FocusSupplier/FocusSupplierForm')
              )
            )
          },
          {
            path: '/audits/user',
            exact: true,
            component: lazy(() =>
              middleware.routeToDisplay(
                [ROUTES_VALIDATIONS.REQUESTED_ROLES],
                {
                  requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                  currentRole: user?.userRole
                },
                import('~/pages/main/Audit/User')
              )
            )
          },
          {
            path: '/',
            exact: true,
            component: () => <Redirect to="/dashboard/analytics" />
          },
          {
            component: () => <Redirect to="/errors/error-404" />
          }
        ]
      }
    ];
  }, [user, configuration]);

  return <BrowserRouter ref={historyRef}>{renderRoutes(routes)}</BrowserRouter>;
};

export default Routes;
